import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {
    AnyImageOpContext,
    AnyImageOpResult,
    AnyImageOpResultOrNode,
    ImageOpOutputs,
    ValueOrNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/highpass/node/definition"
import {Matrix3x2, Size2, Vector2} from "@cm/math"
import {ImageOpNodeAffineTransform} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/affine-transform/node"
import {ImageOpNodeGaussianBlur} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/gaussian-blur/node"
import {ImageOpNodeCreateImage} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/create-image/node"
import {ImageOpNodeMath} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/math/node"
import {assertNever} from "@cm/utils"
import {ImageOpNodeBlend} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blend/node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {CompositeImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/composite-image-op-node"
import {buildLiveSourceMean} from "@app/textures/texture-editor/operator-stack/operators/highpass/helpers/compute-source-mean"

@registerNode
export class ImageOpNodeOperatorGradientRemoval extends CompositeImageOpNode<typeof definition> {
    static nodeClass = "ImageOpNodeOperatorGradientRemoval"
    static definition = definition

    override async execute(evaluator: Evaluator<AnyImageOpContext>, imageOpNodeBag: ImageOpNodeBag): Promise<ImageOpOutputs<typeof definition>> {
        const {
            sourceImage,
            sourceMean,
            maskImage,
            blurSettingsH: blurSettingsHIn,
            blurSettingsV: blurSettingsVIn,
            wrapAround,
            intensity,
            angleOffset: angleOffsetIn,
            displayMode,
            correctionMode,
        } = await evaluator.getAll(this.parameters)

        const blurSettingsH = {
            enabled: blurSettingsHIn.enabled,
            smoothingDistance: Math.round(blurSettingsHIn.smoothingDistance),
        }
        const blurSettingsV = {
            enabled: blurSettingsVIn.enabled,
            smoothingDistance: Math.round(blurSettingsVIn.smoothingDistance),
        }
        const useAngleOffset = (blurSettingsH.enabled || blurSettingsV.enabled) && blurSettingsH.smoothingDistance !== blurSettingsV.smoothingDistance
        const angleOffset = useAngleOffset ? angleOffsetIn : 0

        const applySourceImageConversion = (sourceImage: AnyImageOpResultOrNode) => {
            // const correctLuminanceOnly = false
            // if (correctLuminanceOnly) {
            //     const grayscaleConversionMode = descriptorByTextureType(hints.textureType).isColorData ? "luminance" : "average"
            //     return (this.sourceConversionToGrayscale = nodeWithParameters(this.sourceConversionToGrayscale, ImageOpNodeConvertToGrayscale, {
            //         sourceImage: sourceImage,
            //         mode: grayscaleConversionMode,
            //     }))
            // } else {
            return sourceImage
            // }
        }
        const filterSourceImage = applySourceImageConversion(sourceImage)

        // blur to compute low-pass
        const rotationTransform = new Matrix3x2().rotate(angleOffset)
        const applyRotationAndBlur = (prefix: string, image: ValueOrNode<AnyImageOpResult>, angle: number): ValueOrNode<AnyImageOpResult> => {
            let blurSource: ValueOrNode<AnyImageOpResult>
            if (angle != 0) {
                const computeExpandedSize = (image: AnyImageOpResult) => {
                    const p00 = rotationTransform.multiplyVector(new Vector2(-image.descriptor.width, -image.descriptor.height).mulInPlace(0.5))
                    const p10 = rotationTransform.multiplyVector(new Vector2(image.descriptor.width, -image.descriptor.height).mulInPlace(0.5))
                    const p01 = rotationTransform.multiplyVector(new Vector2(-image.descriptor.width, image.descriptor.height).mulInPlace(0.5))
                    const p11 = rotationTransform.multiplyVector(new Vector2(image.descriptor.width, image.descriptor.height).mulInPlace(0.5))
                    const min = Vector2.min(p00, Vector2.min(p10, Vector2.min(p01, p11)))
                    const max = Vector2.max(p00, Vector2.max(p10, Vector2.max(p01, p11)))
                    return max.sub(min).add(new Vector2(blurSettingsH.smoothingDistance, blurSettingsV.smoothingDistance)).ceil()
                }
                blurSource = imageOpNodeBag.getNode(prefix + "BlurSource", ImageOpNodeAffineTransform, {
                    sourceImage: image,
                    transform: imageOpNodeBag.getLambda(prefix + "Transform", {image}, ({image}) =>
                        new Matrix3x2()
                            .translate(computeExpandedSize(image).mul(0.5))
                            .rotate(angle)
                            .translate(new Vector2(image.descriptor.width, image.descriptor.height).mul(-0.5)),
                    ),
                    addressMode: wrapAround ? "wrap" : "border",
                    resultSize: imageOpNodeBag.getLambda(prefix + "ResultSize", {image}, ({image}) => Size2.fromVector2Like(computeExpandedSize(image))),
                })
            } else {
                blurSource = image
            }
            return imageOpNodeBag.getNode(prefix + "GaussianBlur", ImageOpNodeGaussianBlur, {
                sourceImage: blurSource,
                sigma: imageOpNodeBag.getLambda(prefix + "Sigma", {blurSource}, ({blurSource}) => ({
                    x: (blurSettingsH.enabled ? blurSettingsH.smoothingDistance : blurSource.descriptor.width) / 3,
                    y: (blurSettingsV.enabled ? blurSettingsV.smoothingDistance : blurSource.descriptor.height) / 3,
                })),
                borderMode: wrapAround ? "wrap" : angle != 0 ? "border" : "renormalize",
            })
        }
        let lowpassImage = applyRotationAndBlur("image", filterSourceImage, -angleOffset)
        if (angleOffset != 0) {
            if (!wrapAround) {
                // manual renormalization for rotated border
                const imageMask = imageOpNodeBag.getNode("createMask", ImageOpNodeCreateImage, {
                    descriptor: imageOpNodeBag.getLambda("maskDescriptor", {filterSourceImage}, ({filterSourceImage}) => ({
                        width: filterSourceImage.descriptor.width,
                        height: filterSourceImage.descriptor.height,
                        channels: 1,
                    })),
                    data: 1,
                })
                const blurredRotatedImageMask = applyRotationAndBlur("mask", imageMask, -angleOffset)
                lowpassImage = imageOpNodeBag.getNode("maskRenormalization", ImageOpNodeMath, {
                    operator: "/safe",
                    operandA: lowpassImage,
                    operandB: blurredRotatedImageMask,
                })
            }
            // rotate back
            lowpassImage = imageOpNodeBag.getNode("rotateBack", ImageOpNodeAffineTransform, {
                sourceImage: lowpassImage,
                transform: imageOpNodeBag.getLambda("rotateBackTransform", {sourceImage: sourceImage, lowpassImage}, ({sourceImage, lowpassImage}) =>
                    new Matrix3x2()
                        .translate(new Vector2(sourceImage.descriptor.width, sourceImage.descriptor.height).mul(0.5))
                        .rotate(angleOffset ?? 0)
                        .translate(Vector2.fromSize2Like(lowpassImage.descriptor).mul(-0.5)),
                ),
                resultSize: imageOpNodeBag.getLambda("rotateBackResultSize", {sourceImage: sourceImage}, ({sourceImage}) => sourceImage.descriptor),
            })
        }

        // get optional mask
        if (displayMode === "mask") {
            if (!maskImage) {
                throw new Error("Mask image is required for mask display mode")
            }
            return maskImage
        } else if (displayMode === "low-pass") {
            return await evaluator.get(lowpassImage)
        } else {
            // sourceMean is only supplied for interactive preview (see
            // OperatorHighpass.createImageOpGraph); otherwise (baking) compute it live here, folded
            // into whatever evaluation is already running instead of a separate eager evaluate() pass.
            const resolvedSourceMean = sourceMean ?? buildLiveSourceMean(imageOpNodeBag, "source", filterSourceImage)

            // compute highpass
            let highpassImage: ValueOrNode<AnyImageOpResult>
            if (correctionMode === "offset") {
                const meanMinusLowpass = imageOpNodeBag.getNode("computeMeanMinusLowpass", ImageOpNodeMath, {
                    operator: "-",
                    operandA: resolvedSourceMean,
                    operandB: lowpassImage,
                })
                highpassImage = imageOpNodeBag.getNode("computeOffsetHighpassImage", ImageOpNodeMath, {
                    operator: "+",
                    operandA: sourceImage,
                    operandB: meanMinusLowpass,
                })
            } else if (correctionMode === "modulation") {
                const meanDivLowpass = imageOpNodeBag.getNode("computeMeanDivLowpass", ImageOpNodeMath, {
                    operator: "/safe",
                    operandA: resolvedSourceMean,
                    operandB: lowpassImage,
                })
                highpassImage = imageOpNodeBag.getNode("computeModulationHighpassImage", ImageOpNodeMath, {
                    operator: "*",
                    operandA: sourceImage,
                    operandB: meanDivLowpass,
                })
            } else {
                assertNever(correctionMode)
            }

            // optionally apply mask
            let resultImage: ValueOrNode<AnyImageOpResult>
            if (!maskImage) {
                resultImage = highpassImage
            } else {
                resultImage = imageOpNodeBag.getNode("blendByMask", ImageOpNodeBlend, {
                    backgroundImage: sourceImage,
                    foregroundImage: highpassImage,
                    alpha: maskImage,
                    blendMode: "over",
                    premultipliedAlpha: false,
                })
            }

            // optionally blend by intensity
            if (intensity !== 1) {
                resultImage = imageOpNodeBag.getNode("blendByIntensity", ImageOpNodeBlend, {
                    backgroundImage: sourceImage,
                    foregroundImage: resultImage,
                    alpha: intensity,
                    blendMode: "over",
                    premultipliedAlpha: false,
                })
            }

            return await evaluator.get(resultImage)
        }
    }
}
